import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import toast from 'react-hot-toast';
// NOTE: Super admin password login is intentionally NOT on this page.
// It lives at a secret path defined by REACT_APP_SA_PATH in .env

const API = process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API || '';

// Two tabs visible to everyone: token login | request access
// Super admin login is on a completely separate, secret path.
const TABS = ['Login with Token', 'Request Access'];

export default function Login() {
  const [tab, setTab]       = useState(0);
  const [loading, setLoading] = useState(false);
  const { setToken } = useAuth();
  const navigate = useNavigate();

  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                  height:'100vh', background:'var(--bg)' }}>
      <div style={{ background:'var(--surface)', borderRadius:12,
                    border:'1px solid var(--border)', width:400, overflow:'hidden' }}>

        {/* Header */}
        <div style={{ padding:'28px 32px 0', textAlign:'center' }}>
          <h1 style={{ fontSize:22, fontWeight:700, marginBottom:4 }}>ClassRMM</h1>
          <p style={{ color:'var(--muted)', fontSize:12, marginBottom:20 }}>
            Admin Dashboard
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid var(--border)',
                      padding:'0 32px' }}>
          {TABS.map((label, i) => (
            <button key={i} onClick={() => setTab(i)}
              style={{
                flex: 1, padding:'10px 4px', fontSize:11,
                background:'transparent', border:'none', cursor:'pointer',
                borderBottom: tab === i ? '2px solid var(--accent)' : '2px solid transparent',
                color: tab === i ? 'var(--accent)' : 'var(--muted)',
                fontWeight: tab === i ? 600 : 400,
                transition: 'all .15s',
              }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ padding:'24px 32px 32px' }}>
          {tab === 0 && (
            <TokenLoginForm setLoading={setLoading} loading={loading}
                            setToken={setToken} navigate={navigate} />
          )}
          {tab === 1 && (
            <RequestAccessForm setLoading={setLoading} loading={loading} />
          )}
        </div>
      </div>
    </div>
  );
}


// ── Tab 0: Token Login ────────────────────────────────────────────────────────
function TokenLoginForm({ loading, setLoading, setToken, navigate }) {
  const [token, setTokenVal] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/access/token-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid token');
      setToken(data.access_token, data.expires_at);
      toast.success(`Welcome, ${data.name}`);
      navigate('/');
    } catch (e) {
      toast.error(e.message || 'Token login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <p style={{ color:'var(--muted)', fontSize:12, marginBottom:16, lineHeight:1.6 }}>
        Paste the access token provided by your super admin.
      </p>
      <label style={labelStyle}>Access Token</label>
      <input value={token} onChange={e => setTokenVal(e.target.value)} required
             placeholder="Paste your token here…"
             style={{ ...inputStyle, fontFamily:'monospace', marginBottom:20 }} />
      <button type="submit" disabled={loading} style={submitBtn}>
        {loading ? 'Verifying…' : 'Enter Dashboard'}
      </button>
    </form>
  );
}


// ── Tab 1: Request Access ─────────────────────────────────────────────────────
function RequestAccessForm({ loading, setLoading }) {
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [telegram, setTelegram] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/access/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, telegram_username: telegram }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');
      toast.success('Request submitted!');
      setSubmitted(true);
    } catch (e) {
      toast.error(e.message || 'Could not submit request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ textAlign:'center', padding:'16px 0' }}>
        <div style={{ fontSize:32, marginBottom:12 }}>✅</div>
        <p style={{ fontWeight:600, marginBottom:8 }}>Request Submitted</p>
        <p style={{ color:'var(--muted)', fontSize:13, lineHeight:1.7 }}>
          The super admin has been notified. Once approved, you'll receive an
          access token. Come back to the <strong>Login with Token</strong> tab to enter.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <p style={{ color:'var(--muted)', fontSize:12, marginBottom:16, lineHeight:1.6 }}>
        Fill in your details. The super admin will review your request and send you
        a token via Telegram or email.
      </p>
      <label style={labelStyle}>Full Name</label>
      <input value={name} onChange={e => setName(e.target.value)} required
             placeholder="e.g. Jane Smith" style={inputStyle} />
      <label style={labelStyle}>Email Address</label>
      <input value={email} onChange={e => setEmail(e.target.value)} type="email" required
             placeholder="e.g. jane@school.edu" style={inputStyle} />
      <label style={labelStyle}>Telegram Username <span style={{color:'var(--muted)'}}>(optional)</span></label>
      <input value={telegram} onChange={e => setTelegram(e.target.value)}
             placeholder="e.g. @janeadmin" style={{ ...inputStyle, marginBottom:20 }} />
      <button type="submit" disabled={loading} style={submitBtn}>
        {loading ? 'Submitting…' : 'Submit Request'}
      </button>
    </form>
  );
}


// ── Styles ────────────────────────────────────────────────────────────────────
const inputStyle = {
  display:'block', width:'100%', marginBottom:14, padding:'10px 12px',
  background:'var(--bg)', border:'1px solid var(--border)', borderRadius:6,
  color:'var(--text)', fontSize:14, boxSizing:'border-box',
};

const labelStyle = {
  display:'block', marginBottom:4, color:'var(--muted)', fontSize:12,
};

const submitBtn = {
  width:'100%', padding:'10px 0', background:'var(--accent)',
  border:'none', borderRadius:6, color:'#fff', fontWeight:600,
  cursor:'pointer', fontSize:14,
};
